import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useCatalog, useHeroText, useSiteMediaState, useSeo } from "@/lib/site";

const IMG_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";
const CHUNK = 5 * 1024 * 1024;

const useRun = (keys) => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return [busy, run];
};

const upload = (url, file) => { const fd = new FormData(); fd.append("file", file); return api.post(url, fd); };

const FileButton = ({ testId, onFile, busy, accept = IMG_ACCEPT, label = "Cambiar foto" }) => (
  <label className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded-md bg-[#1C1715] px-4 text-sm text-[#FDFBF7] transition-colors hover:bg-[#B18A4B] focus-within:ring-2 focus-within:ring-[#B18A4B] ${busy ? "pointer-events-none opacity-60" : ""}`}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Subiendo…" : label}
    <input type="file" accept={accept} className="sr-only" data-testid={testId} disabled={busy}
      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
  </label>
);

const Section = ({ title, hint, children, testId }) => (
  <section data-testid={testId} className="mt-12">
    <h2 className="font-serif-display text-2xl font-light">{title}</h2>
    {hint && <p className="mt-1 max-w-2xl text-sm text-[#5C534C]">{hint}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

const MediaCard = ({ m, aspect = "aspect-[4/5]", editTitle, fit = "object-cover" }) => {
  const [busy, run] = useRun(["site-media"]);
  const [f, setF] = useState({ title: m.title, alt: m.alt });
  useEffect(() => setF({ title: m.title, alt: m.alt }), [m]);
  return (
    <article data-testid={`media-item-${m.key}`} className="flex flex-col border border-[#1C1715]/10 bg-white p-4">
      <div className={`overflow-hidden bg-[#EAE7E2] ${aspect}`}><img src={m.url} alt={m.alt} data-testid={`media-preview-${m.key}`} className={`h-full w-full ${fit}`} style={{ objectPosition: m.pos || "50% 50%" }} /></div>
      <p className="mt-3 text-sm font-medium">{m.title}</p>
      {editTitle && (
        <label className="mt-3 block text-xs uppercase tracking-[0.14em] text-[#5C534C]">Título
          <Input data-testid={`media-title-${m.key}`} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" />
        </label>
      )}
      <label className="mt-3 block text-xs uppercase tracking-[0.14em] text-[#5C534C]">Descripción de la foto
        <Input data-testid={`media-alt-${m.key}`} value={f.alt} onChange={(e) => setF({ ...f, alt: e.target.value })} className="mt-1" />
      </label>
      <div className="mt-4 flex flex-wrap gap-2">
        <FileButton testId={`media-file-${m.key}`} busy={busy} onFile={(file) => run(() => upload(`/admin/site-media/${m.key}/image`, file), "Foto cambiada")} />
        <Button variant="outline" size="sm" className="h-9" data-testid={`media-save-${m.key}`} disabled={busy}
          onClick={() => run(() => api.put(`/admin/site-media/${m.key}`, f), "Textos guardados")}>Guardar textos</Button>
      </div>
    </article>
  );
};

const Grid = ({ items, cols = "sm:grid-cols-2 xl:grid-cols-3", ...rest }) => (
  <div className={`grid gap-4 ${cols}`}>{items.map((m) => <MediaCard key={m.key} m={m} {...rest} />)}</div>
);

const HeroTextEditor = () => {
  const text = useHeroText();
  const [f, setF] = useState(null);
  const [busy, run] = useRun(["hero-text"]);
  useEffect(() => { if (text) setF(text); }, [text]);
  if (!f) return null;
  const field = (k, label) => (
    <label className="block text-xs uppercase tracking-[0.14em] text-[#5C534C]">{label}
      <Input data-testid={`hero-text-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 normal-case tracking-normal" />
    </label>
  );
  return (
    <div data-testid="hero-text-editor" className="grid gap-4 border border-[#1C1715]/10 bg-white p-5 lg:grid-cols-3">
      {field("title", "Título principal")}
      {field("title_em", "Título · parte en cursiva")}
      {field("support", "Frase de apoyo")}
      <div className="lg:col-span-3"><Button data-testid="hero-text-save" disabled={busy} onClick={() => run(() => api.put("/admin/site-content/hero", f), "Textos de portada guardados")}>Guardar textos de portada</Button></div>
    </div>
  );
};

const ProductPhoto = ({ slug, o, ph, i, n }) => {
  const [busy, run] = useRun(["catalog"]);
  const base = `/admin/products/${slug}/photos/${o}/${ph.id}`;
  const move = (d) => run(() => api.post(`${base}/move`, { direction: d }), "Orden actualizado");
  return (
    <div data-testid={`product-photo-${slug}-${o}-${i}`} className="flex flex-col border border-[#1C1715]/10 bg-white p-3">
      <img src={ph.url} alt="" className="aspect-square w-full bg-[#F4F1EC] object-contain" />
      <p className="mt-2 text-xs text-[#5C534C]">{i === 0 ? "Foto principal" : `Foto ${i + 1}`}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <FileButton testId={`product-photo-file-${slug}-${o}-${i}`} busy={busy} onFile={(f) => run(() => upload(`${base}/image`, f), "Foto cambiada")} />
        <Button size="icon" variant="outline" className="h-9 w-9" aria-label="Mover a la izquierda" data-testid={`product-photo-left-${slug}-${o}-${i}`} disabled={busy || i === 0} onClick={() => move(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="h-9 w-9" aria-label="Mover a la derecha" data-testid={`product-photo-right-${slug}-${o}-${i}`} disabled={busy || i === n - 1} onClick={() => move(1)}><ArrowRight className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="h-9 w-9 text-red-700" aria-label="Eliminar foto" data-testid={`product-photo-delete-${slug}-${o}-${i}`} disabled={busy}
          onClick={() => window.confirm("¿Eliminar esta foto?") && run(() => api.delete(base), "Foto eliminada")}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const AddPhotoSlot = ({ slug, o }) => {
  const [busy, run] = useRun(["catalog"]);
  return (
    <div className="grid aspect-square place-items-center border border-dashed border-[#1C1715]/25 bg-[#FDFBF7] p-3">
      <FileButton testId={`product-photo-add-${slug}-${o}`} busy={busy} label="Subir foto" onFile={(f) => run(() => upload(`/admin/products/${slug}/photos/${o}`, f), "Foto agregada")} />
    </div>
  );
};

const ProductPhotos = ({ p }) => (
  <div data-testid={`product-photos-${p.slug}`} className="border border-[#1C1715]/10 bg-[#FDFBF7] p-5">
    <h3 className="font-serif-display text-xl">Talla {p.size}</h3>
    {["vertical", "horizontal"].map((o) => {
      const photos = p.photos?.[o] || [];
      return (
        <div key={o} className="mt-4">
          <p className="mb-2 text-xs uppercase tracking-[0.14em] text-[#8C827A]">{o === "vertical" ? "Vertical" : "Horizontal"} · {photos.length}/4</p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {photos.map((ph, i) => <ProductPhoto key={ph.id} slug={p.slug} o={o} ph={ph} i={i} n={photos.length} />)}
            {photos.length < 4 && <AddPhotoSlot slug={p.slug} o={o} />}
          </div>
        </div>
      );
    })}
  </div>
);

const VideoEditor = ({ m }) => {
  const [busy, run] = useRun(["site-media"]);
  const [pct, setPct] = useState(0);
  const send = async (file) => {
    if (!/\.mp4$/i.test(file.name) && file.type !== "video/mp4") throw new Error("El video debe estar en formato MP4");
    const id = crypto.randomUUID(), total = Math.ceil(file.size / CHUNK);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", id); fd.append("index", i); fd.append("total", total); fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK));
      await api.post(`/admin/site-media/${m.key}/video-chunk`, fd);
      setPct(Math.round(((i + 1) / total) * 100));
    }
  };
  return (
    <article data-testid="media-item-install-video" className="flex flex-col border border-[#1C1715]/10 bg-white p-4">
      <video key={m.url} src={m.url} controls preload="metadata" className="aspect-[9/16] max-h-[420px] w-full bg-[#EAE7E2] object-cover" />
      <p className="mt-3 text-sm font-medium">{m.title}</p>
      <p className="mt-1 text-xs text-[#5C534C]">Formato MP4, hasta 200 MB.</p>
      <div className="mt-4 flex items-center gap-3">
        <FileButton testId="media-file-install-video" busy={busy} accept="video/mp4" label="Cambiar video" onFile={(f) => { setPct(0); run(() => send(f), "Video cambiado"); }} />
        {busy && <span data-testid="video-upload-progress" className="text-sm text-[#5C534C]">{pct}%</span>}
      </div>
    </article>
  );
};

export default function SiteMediaAdmin() {
  useSeo({ title: "Contenido del sitio | Estudio LUMINA", noindex: true });
  const { media } = useSiteMediaState();
  const { data: catalog } = useCatalog();
  const all = Object.values(media);
  const group = (g) => all.filter((m) => m.group === g).sort((a, b) => a.order - b.order);
  return (
    <div data-testid="site-media-admin" className="min-h-screen bg-[#FDFBF7] px-6 py-10 sm:px-10">
      <Link to="/studio" data-testid="site-media-back-link" className="text-xs uppercase tracking-[0.2em] text-[#5C534C] hover:text-[#1C1715]">← Volver al estudio</Link>
      <h1 className="mt-4 font-serif-display text-3xl sm:text-4xl font-light">Contenido del sitio</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#5C534C]">Cada cambio se publica al instante en la página, sin tocar código.</p>

      <Section testId="admin-section-hero" title="Portada" hint="Texto principal, pared de fondo y las 8 fotos de las láminas animadas.">
        <HeroTextEditor />
        <div className="mt-4"><Grid items={group("hero-wall")} aspect="aspect-video" cols="md:grid-cols-2" /></div>
        <div className="mt-4"><Grid items={group("hero-photos")} aspect="aspect-[3/4]" cols="grid-cols-2 md:grid-cols-4" /></div>
      </Section>

      <Section testId="admin-section-sizes" title="Elige tu LUMINA" hint="Fotos de las tarjetas S, M y L de la página de inicio.">
        <Grid items={group("sizes")} />
      </Section>

      <Section testId="admin-section-compare" title="Productos · Elige tu LUMINA" hint="Una foto para cada talla y orientación en la comparación de tamaños de /productos. Se muestra completa, sin recortes.">
        <Grid items={group("compare")} aspect="aspect-[4/3]" fit="object-contain" cols="grid-cols-2 xl:grid-cols-3" />
      </Section>

      <Section testId="admin-section-products" title="Páginas de producto" hint="4 fotos verticales y 4 horizontales por talla. La primera se ve grande en la página del producto y las otras 3 debajo.">
        <div className="space-y-4">{(catalog?.products || []).map((p) => <ProductPhotos key={p.slug} p={p} />)}</div>
      </Section>

      <Section testId="admin-section-story" title="Nuestra historia" hint="Fotos del equipo, el taller y el cuidado de cada pedido.">
        <Grid items={group("story")} editTitle />
      </Section>

      <Section testId="admin-section-video" title="Video de instalación" hint="Se muestra en la página de inicio y en Cómo funciona.">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {media["install-video"] && <VideoEditor m={media["install-video"]} />}
          {group("video").filter((m) => m.kind !== "video").map((m) => <MediaCard key={m.key} m={m} aspect="aspect-[9/16] max-h-[420px]" />)}
        </div>
      </Section>
    </div>
  );
}
